/** @prettier */
import { List } from 'immutable';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { Fragment, useCallback, useEffect, useState } from 'react';

import async from '@xanadu/ui-shared/components/asyncComponent';
import Button from '@xanadu/ui-shared/components/widgets/Button';
import { track } from '@xanadu/ui-shared/interfaces/woopra';

import { UK_COUNTRY_CODE } from '../../../constants';
import { DOCUMENT_TYPE_LABELS } from '../../AccountPage/components/AccountVerification/constants';
import {
  ACCEPTED_MEDIA_TYPES_MESSAGE,
  MAX_FILE_SIZE_LIMIT,
  isMediaTypeAccepted,
} from '../constants';

import useExternalDocsUploader from '../hooks/useExternalDocsUploader';

import UploadSection from '../UploadSection';

import styles from './UploadDocuments.module.scss';

const AsyncExternalDocsUploader = async(() =>
  import('../ExternalDocsUploader').then((module) => module.default)
);

const UploadDocuments = ({
  closeModal,
  displayModal,
  depositRequest,
  uploadDocsRequest,
  uploadDocsReset,
  userRegistered,
  error,
  loading,
  POIStatus,
  POAStatus,
  documentType,
  countryId,
  jobTitle,
  salaryRange,
  journeyType,
}) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [groupedFiles, setGroupedFiles] = useState();
  const [fileTypeError, setFileTypeError] = useState();
  const [safariFileTypeError, setSafariFileTypeError] = useState();
  const [sizeError, setSizeError] = useState();
  const [fileNumberError, setFileNumberError] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const [showFallback, setShowFallback] = useState(false);

  const showExternalDocsUploader =
    useExternalDocsUploader({ POIStatus, POAStatus }) &&
    !showFallback &&
    documentType !== DOCUMENT_TYPE_LABELS.OTHER;

  const isUkUser = countryId === UK_COUNTRY_CODE;

  useEffect(() => {
    if (selectedFiles.length > 0) {
      const files = new List(selectedFiles);
      setGroupedFiles(files.groupBy((file) => file.docType));
      if (selectedFiles.some((x) => x.sizeError)) {
        const filesWithSizeErrors = files.filter((x) => x.sizeError);
        const mappedFiles = filesWithSizeErrors.groupBy((file) => file.docType);
        const [...keys] = mappedFiles.keys();
        setSizeError([...keys]);
      } else if (selectedFiles.some((x) => x.fileTypeError)) {
        const filesWithFileTypeErrors = files.filter((x) => x.fileTypeError);
        const mappedFiles = filesWithFileTypeErrors.groupBy((file) => file.docType);
        const [...keys] = mappedFiles.keys();
        setSafariFileTypeError([...keys]);
      } else {
        setSizeError();
        setSafariFileTypeError();
      }
    } else {
      setSizeError();
      setSafariFileTypeError();
      setGroupedFiles();
    }
  }, [selectedFiles, selectedFiles.length]);

  useEffect(() => {
    if (
      selectedFiles.length > 0 &&
      !loading &&
      !fileTypeError &&
      !safariFileTypeError &&
      !sizeError
    ) {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
  }, [selectedFiles.length, loading, fileTypeError, safariFileTypeError, sizeError]);

  useEffect(() => {
    if (isUkUser && (!jobTitle || !salaryRange)) {
      displayModal({
        show: true,
        section: 'occupation-and-salary-range',
      });
    }
  }, [jobTitle, salaryRange]);

  useEffect(
    () => () => {
      uploadDocsReset();
    },
    []
  );

  const onCloseModal = useCallback(() => {
    let timer;
    closeModal();

    track('Registration', 'Upload Docs - Drop Off', { reason: error });

    if (userRegistered) {
      timer = setTimeout(() => {
        depositRequest(true);
      }, 300);
    }

    return () => {
      clearTimeout(timer);
    };
  }, [error, userRegistered]);

  const handleExternalDocsUploaderError = useCallback(() => {
    setShowFallback(true);
  }, []);

  const handleSendFiles = () => {
    track('Registration', 'Upload Docs');
    uploadDocsRequest(selectedFiles, { userRegistered });
  };

  const dragEnter = (e, { type }) => {
    setFileNumberError(false);
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      for (let i = 0; i < e.dataTransfer.items.length; i++) {
        if (!isMediaTypeAccepted(e.dataTransfer.items[i].type)) {
          setFileTypeError({ message: ACCEPTED_MEDIA_TYPES_MESSAGE, type });
        }
      }
    }
  };

  const dragLeave = () => {
    setFileNumberError(false);
    setFileTypeError();
    setSafariFileTypeError();
  };

  const handleDrop = (files, type) => {
    if (!fileTypeError) {
      const fileList = selectedFiles;

      for (let i = 0; i < files.length; i++) {
        if (!files[i].name) return;
        const currentFile = files[i];
        const currentNumberFiles =
          groupedFiles && groupedFiles.get(type) ? groupedFiles.get(type).size : 0;

        if (currentNumberFiles + files.length > 6) {
          setFileNumberError(true);
        } else {
          if (!isMediaTypeAccepted(currentFile.type)) {
            currentFile.fileTypeError = true;
          }

          currentFile.docType = type;
          currentFile.uploadTime = moment().unix();
          if (currentFile.size / 1e6 > MAX_FILE_SIZE_LIMIT) {
            currentFile.sizeError = true;
          }
          fileList.push(currentFile);
        }
      }
      setSelectedFiles([...fileList]);
    }
    setFileTypeError();
  };

  const fileDrop = (e, { type }) => {
    if (fileTypeError && fileTypeError.type === type) {
      setFileTypeError();
      e.dataTransfer.clearData();
    } else if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleDrop(e.dataTransfer.files, type);
      e.dataTransfer.clearData();
    }
  };

  const removeFile = (file) => {
    setFileNumberError(false);
    const fileList = [...selectedFiles];
    if (fileList.length > 0) {
      const filteredList = fileList.filter(
        (x) => !(x.name === file.name && x.uploadTime === file.uploadTime)
      );

      setSelectedFiles([...filteredList]);
    } else {
      setSelectedFiles([]);
    }
  };

  const triggerInput = ({ ref }) => {
    setFileNumberError(false);
    if (ref.current) {
      ref.current.click();
    }
  };

  let footerContent = (
    <div className={styles.footerText}>
      <span>
        Please ensure that the images are clear and all information is visible.
        <br />
        {ACCEPTED_MEDIA_TYPES_MESSAGE}
      </span>
      <b> {`${MAX_FILE_SIZE_LIMIT}MB max file size.`}</b>
    </div>
  );

  if (fileNumberError) {
    footerContent = (
      <div className={styles.fileNumberError}>
        A maximum of 6 files per section can be uploaded at once.
      </div>
    );
  } else if (error) {
    footerContent = <div className={styles.fileNumberError}>{error}</div>;
  }

  if (showExternalDocsUploader) {
    return (
      <AsyncExternalDocsUploader
        closeModal={closeModal}
        onError={handleExternalDocsUploaderError}
        journeyType={journeyType}
      />
    );
  }

  return (
    <div className={styles.uploadDocuments}>
      <div className={styles.textContainer}>
        <div className={styles.textTop}>Upload Documents</div>
      </div>
      <div className={styles.uploadContainer}>
        {documentType ? (
          <Fragment>
            {documentType === DOCUMENT_TYPE_LABELS.POI && (
              <UploadSection
                groupedFiles={groupedFiles}
                fileError={fileTypeError}
                safariFileError={safariFileTypeError}
                sizeError={sizeError}
                type="id-docs"
                title="Proof of Identity"
                triggerInput={triggerInput}
                removeFile={removeFile}
                fileDrop={fileDrop}
                handleDrop={handleDrop}
                dragEnter={dragEnter}
                dragLeave={dragLeave}
                icon="icon-proofofidentity.svg"
                required={userRegistered}
              />
            )}
            {documentType === DOCUMENT_TYPE_LABELS.POA && (
              <UploadSection
                groupedFiles={groupedFiles}
                fileError={fileTypeError}
                safariFileError={safariFileTypeError}
                sizeError={sizeError}
                type="address-docs"
                title="Proof of Address"
                triggerInput={triggerInput}
                removeFile={removeFile}
                fileDrop={fileDrop}
                handleDrop={handleDrop}
                dragEnter={dragEnter}
                dragLeave={dragLeave}
                required={userRegistered}
                icon="icon-proofofaddress.svg"
              />
            )}
            {documentType === DOCUMENT_TYPE_LABELS.OTHER && (
              <UploadSection
                groupedFiles={groupedFiles}
                fileError={fileTypeError}
                safariFileError={safariFileTypeError}
                sizeError={sizeError}
                type="other-docs"
                title="Other Documents"
                triggerInput={triggerInput}
                removeFile={removeFile}
                fileDrop={fileDrop}
                handleDrop={handleDrop}
                dragEnter={dragEnter}
                dragLeave={dragLeave}
                icon="icon-otherdoc.svg"
              />
            )}
          </Fragment>
        ) : (
          <Fragment>
            <UploadSection
              groupedFiles={groupedFiles}
              fileError={fileTypeError}
              safariFileError={safariFileTypeError}
              sizeError={sizeError}
              type="id-docs"
              title="Proof of Identity"
              triggerInput={triggerInput}
              removeFile={removeFile}
              fileDrop={fileDrop}
              handleDrop={handleDrop}
              dragEnter={dragEnter}
              dragLeave={dragLeave}
              icon="icon-proofofidentity.svg"
              required={userRegistered}
            />
            <UploadSection
              groupedFiles={groupedFiles}
              fileError={fileTypeError}
              safariFileError={safariFileTypeError}
              sizeError={sizeError}
              type="address-docs"
              title="Proof of Address"
              triggerInput={triggerInput}
              removeFile={removeFile}
              fileDrop={fileDrop}
              handleDrop={handleDrop}
              dragEnter={dragEnter}
              dragLeave={dragLeave}
              required={userRegistered}
              icon="icon-proofofaddress.svg"
            />
            {!userRegistered && (
              <UploadSection
                groupedFiles={groupedFiles}
                fileError={fileTypeError}
                safariFileError={safariFileTypeError}
                sizeError={sizeError}
                type="other-docs"
                title="Other Documents"
                triggerInput={triggerInput}
                removeFile={removeFile}
                fileDrop={fileDrop}
                handleDrop={handleDrop}
                dragEnter={dragEnter}
                dragLeave={dragLeave}
                icon="icon-otherdoc.svg"
              />
            )}
          </Fragment>
        )}
      </div>
      <div className={styles.bottomSection}>
        {footerContent}
        <div className={styles.buttons}>
          <span role="button" onClick={onCloseModal}>
            {"I'll do it later"}
          </span>
          <Button
            sizeType="medium"
            buttonType="primary"
            onClick={handleSendFiles}
            disabled={disabled}
            loading={loading}
          >
            {error ? 'Try Again' : 'Send Files'}
          </Button>
        </div>
      </div>
    </div>
  );
};

UploadDocuments.propTypes = {
  uploadDocsRequest: PropTypes.func.isRequired,
  uploadDocsReset: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  displayModal: PropTypes.func.isRequired,
  depositRequest: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  userRegistered: PropTypes.bool.isRequired,
  POIStatus: PropTypes.string.isRequired,
  POAStatus: PropTypes.string.isRequired,
  documentType: PropTypes.string.isRequired,
  countryId: PropTypes.number.isRequired,
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  jobTitle: PropTypes.string,
  salaryRange: PropTypes.string,
  journeyType: PropTypes.string,
};

export default UploadDocuments;
